import { i18n } from '@/i18n';
import { DOCUMENT_TYPE } from '@/typings/enums/DOCUMENT_TYPE';
import { TAG_LINK_TYPE } from '@/typings/enums/TAG_LINK_TYPE';
import { USER_TYPE } from '@/typings/enums/USER_TYPE';
import { getApi } from '@/utils/ApiTool';
import { itemHas } from '@orion.ui/orion';
import BaseDocumentService from './BaseDocumentService';
import useSelfService from './SelfService';

type ReadableDocumentData = {
	title: string
	title_plural: string
	title_short: string
	title_short_plural: string // max 17 char.
	icon: string
};

type ReadableDocument = {
	title: string
	title_short: string
	icon: string
};

class DocumentService extends BaseDocumentService<Armado.ArmadoDocument> {

	static get data () {
		return new Map<keyof typeof DOCUMENT_TYPE, ReadableDocumentData>([
			[
				'Bill', {
					title: i18n.global.t('documents.bill'),
					title_plural: i18n.global.t('documents.bills'),
					title_short: i18n.global.t('documents.bill'),
					title_short_plural: i18n.global.t('documents.bills'),
					icon: `icon-money-cash-file-dollar`,
				},
			],
			[
				'Certificate', {
					title: i18n.global.t('documents.workCertificate'),
					title_plural: i18n.global.t('documents.workCertificates'),
					title_short: i18n.global.t('documents.workCertificateShort'),
					title_short_plural: i18n.global.t('documents.workCertificateShort'),
					icon: `icon-interface-file-bookmark`,
				},
			],
			[
				'ClientContract', {
					title: i18n.global.t('documents.clientContract'),
					title_plural: i18n.global.t('documents.clientContracts'),
					title_short: i18n.global.t('documents.clientContract'),
					title_short_plural: i18n.global.t('documents.clientContracts'),
					icon: `icon-shopping-business-handshake`,
				},
			],
			[
				'ClientProvisionDocument', {
					title: `${i18n.global.t('documents.provision.document')} ${useSelfService().self.isAgency() ? i18n.global.t('documents.clients.of') : ''}`,
					title_plural: `${i18n.global.t('documents.provision.documents')} ${useSelfService().self.isAgency() ? i18n.global.t('documents.clients.of') : ''}`,
					title_short: i18n.global.t('documents.provisionDoc'),
					title_short_plural: i18n.global.t('documents.provisionDoc'),
					icon: `icon-programming-script-2`,
				},
			],
			[
				'ContractProlongation', {
					title: i18n.global.t('business.contractExtension'),
					title_plural: i18n.global.t('documents.contractExtensions'),
					title_short: i18n.global.t('documents.contractExtensionShort'),
					title_short_plural: i18n.global.t('documents.contractExtensionsShort'),
					icon: `icon-shopping-business-handshake`,
				},
			],
			[
				'HandicappedWorkerList', {
					title: i18n.global.t('documents.disabledWorkersList'),
					title_plural: i18n.global.t('documents.disabledWorkersLists'),
					title_short: i18n.global.t('documents.disabledWorkersListShort'),
					title_short_plural: i18n.global.t('documents.disabledWorkersListsShort'),
					icon: `icon-programming-script-2`,
				},
			],
			[
				'Order', {
					title: i18n.global.t('documents.order'),
					title_plural: i18n.global.t('documents.orders'),
					title_short: i18n.global.t('documents.order'),
					title_short_plural: i18n.global.t('documents.orders'),
					icon: `icon-shopping-cart-1`,
				},
			],
			[
				'PoleEmploiAttestation', {
					title: i18n.global.t('documents.employerAttestation'),
					title_plural: i18n.global.t('documents.employerAttestations'),
					title_short: i18n.global.t('documents.unemploymentCert'),
					title_short_plural: i18n.global.t('documents.unemploymentCert'),
					icon: `icon-interface-file-bookmark`,
				},
			],
			[
				'Register', {
					title: i18n.global.t('documents.personnelRegister'),
					title_plural: i18n.global.t('documents.personnelRegistersShort'),
					title_short: i18n.global.t('documents.personnelRegister'),
					title_short_plural: i18n.global.t('documents.personnelRegistersShort'),
					icon: `icon-programming-script-2`,
				},
			],
			[
				'SalarySlip', {
					title: i18n.global.t('documents.paySlip'),
					title_plural: i18n.global.t('common.paySlips'),
					title_short: i18n.global.t('documents.paySlipShort'),
					title_short_plural: i18n.global.t('documents.paySlipsShort'),
					icon: `icon-money-cash-file-dollar`,
				},
			],
			[
				'TempWorkerContract', {
					title: i18n.global.t('documents.tempWorkerContract'),
					title_plural: i18n.global.t('documents.tempWorkerContracts'),
					title_short: i18n.global.t('documents.tempContract'),
					title_short_plural: i18n.global.t('documents.tempContracts'),
					icon: `icon-shopping-business-handshake`,
				},
			],
			[
				'TempWorkerProvisionDocument', {
					title: i18n.global.t('documents.tempWorker.provisionDocument'),
					title_plural: i18n.global.t('documents.tempWorker.provisionDocuments'),
					title_short: i18n.global.t('documents.provisionDoc'),
					title_short_plural: i18n.global.t('documents.provisionDoc'),
					icon: `icon-programming-script-2`,
				},
			],
			[
				'TimeTicket', {
					title: i18n.global.t('documents.timeTicket'),
					title_plural: i18n.global.t('common.timeTickets'),
					title_short: i18n.global.t('documents.timeTicket'),
					title_short_plural: i18n.global.t('common.timeTickets'),
					icon: `icon-interface-time-book-2`,
				},
			],
			[
				'TripAttestation', {
					title: i18n.global.t('documents.travelJustification'),
					title_plural: i18n.global.t('documents.travelJustifications'),
					title_short: i18n.global.t('documents.travelJustification'),
					title_short_plural: i18n.global.t('documents.travelJustification'),
					icon: `icon-travel-map-triangle-flag`,
				},
			],
			[
				'WithholdingTaxAttestation', {
					title: i18n.global.t('documents.taxCertificate'),
					title_plural: i18n.global.t('common.taxCertificates'),
					title_short: i18n.global.t('documents.taxCert'),
					title_short_plural: i18n.global.t('documents.taxCert'),
					icon: `icon-legal-justice-scale-2`,
				},
			],
		]);
	}

	get api () {
		const targetApi = getApi(this.userType, this.documentType);
		if (itemHas(targetApi, 'view') && itemHas(targetApi, 'download')) {
			return targetApi;
		}
		else {
			return {
				view: () => undefined,
				download: () => undefined,
			};
		}
	}

	static getEnumTypeKey (enumTypeValue: DOCUMENT_TYPE) {
		for (const key in DOCUMENT_TYPE) {
			if (DOCUMENT_TYPE[key as keyof typeof DOCUMENT_TYPE] === enumTypeValue) return key as keyof typeof DOCUMENT_TYPE;
		}
	}

	static getReadableDocumentByEnumKey (key: keyof typeof DOCUMENT_TYPE, qty = 0) {
		const data = useDocumentService().data.get(key);
		if (!data) return;

		const res: ReadableDocument = {
			icon: data.icon,
			title_short: qty > 1 ? data.title_short_plural : data.title_short,
			title: qty > 1 ? data.title_plural : data.title,
		};

		return res;
	}

	static getTagLinkType (documentType: DOCUMENT_TYPE) {
		const key = DocumentService.getEnumTypeKey(documentType) as keyof typeof TAG_LINK_TYPE;
		if (key && TAG_LINK_TYPE[key] !== undefined) return TAG_LINK_TYPE[key];
	}

	static getDocumentTypeFromTagLink (tagLinkType: TAG_LINK_TYPE) {
		let tagKey;

		for (const key in TAG_LINK_TYPE) {
			if (TAG_LINK_TYPE[key as keyof typeof TAG_LINK_TYPE] === +tagLinkType) tagKey = key;
		}

		if (tagKey && DOCUMENT_TYPE[tagKey as keyof typeof DOCUMENT_TYPE] !== undefined) {
			return DOCUMENT_TYPE[tagKey as keyof typeof DOCUMENT_TYPE] as unknown as DOCUMENT_TYPE;
		}
	}

	private userType!: USER_TYPE;

	private documentType!: DOCUMENT_TYPE;

	constructor (userType: USER_TYPE, documentType: DOCUMENT_TYPE) {
		super();
		this.userType = userType;
		this.documentType = documentType;
	}

}

export default function useDocumentService (): typeof DocumentService;
export default function useDocumentService (userType: USER_TYPE, documentType: DOCUMENT_TYPE): DocumentService;
export default function useDocumentService (userType?: USER_TYPE, documentType?: DOCUMENT_TYPE): typeof DocumentService | DocumentService {
	if (userType !== undefined && documentType !== undefined) {
		return new DocumentService(userType, documentType);
	}
	return DocumentService;
}
